import React, { useRef, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { Modal } from '../../components/UI/Modal';
import type { FormImageSource } from '../../types/formFieldSettings';

export interface FormImageInsertResult {
  imageSource: FormImageSource;
  objectKey?: string;
  externalUrl?: string;
  previewUrl: string;
}

interface FormInsertImageModalProps {
  isOpen: boolean;
  formId: string;
  onClose: () => void;
  onConfirm: (result: FormImageInsertResult) => void;
  uploadImage: (formId: string, file: File) => Promise<{ objectKey: string; url: string }>;
}

type ImageTab = 'upload' | 'url';

export const FormInsertImageModal: React.FC<FormInsertImageModalProps> = ({
  isOpen,
  formId,
  onClose,
  onConfirm,
  uploadImage,
}) => {
  const [tab, setTab] = useState<ImageTab>('upload');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setTab('upload');
    setUrl('');
    setError(null);
    setIsUploading(false);
    setIsDragging(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File | null) => {
    if (!file)
    {
      return;
    }

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type.toLowerCase()))
    {
      setError('Formato inválido. Use JPG, PNG ou WebP.');
      return;
    }

    if (file.size > 2 * 1024 * 1024)
    {
      setError('A imagem deve ter no máximo 2 MB.');
      return;
    }

    setError(null);
    setIsUploading(true);
    try
    {
      const uploaded = await uploadImage(formId, file);
      onConfirm({
        imageSource: 'Upload',
        objectKey: uploaded.objectKey,
        previewUrl: uploaded.url,
      });
      reset();
    }
    catch
    {
      setError('Não foi possível enviar a imagem.');
    }
    finally
    {
      setIsUploading(false);
    }
  };

  const handleConfirmUrl = () => {
    const trimmed = url.trim();
    if (!trimmed)
    {
      setError('Informe a URL da imagem.');
      return;
    }

    try
    {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
      {
        setError('Informe uma URL http ou https válida.');
        return;
      }
    }
    catch
    {
      setError('Informe uma URL válida.');
      return;
    }

    onConfirm({
      imageSource: 'Url',
      externalUrl: trimmed,
      previewUrl: trimmed,
    });
    reset();
  };

  return (
    <Modal isOpen={isOpen} title="Inserir imagem" onClose={handleClose}>
      <div className="mb-4 flex gap-4 border-b border-gray-200">
        <button
          type="button"
          className={`pb-2 text-sm font-medium ${
            tab === 'upload'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() =>
          {
            setTab('upload');
            setError(null);
          }}
        >
          Fazer upload
        </button>
        <button
          type="button"
          className={`pb-2 text-sm font-medium ${
            tab === 'url'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() =>
          {
            setTab('url');
            setError(null);
          }}
        >
          Por URL
        </button>
      </div>

      {tab === 'upload' && (
        <div
          className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-10 ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
          }`}
          onDragOver={(e) =>
          {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) =>
          {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files?.[0] ?? null;
            void handleFile(file);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mb-3 h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 15a4 4 0 004 4h10a4 4 0 004-4v-1a4 4 0 00-4-4h-.586l-.707-.707A2 2 0 0014.586 8H9.414a2 2 0 00-1.414.586L7.293 9.293A1 1 0 016.586 10H6a4 4 0 00-4 4v1z"
            />
          </svg>
          <Button
            variant="primary"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? 'Enviando...' : 'Procurar'}
          </Button>
          <p className="mt-3 text-sm text-gray-500">ou arraste um arquivo até aqui</p>
          <p className="mt-1 text-xs text-gray-400">JPG, PNG ou WebP · máx. 2 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) =>
            {
              const file = e.target.files?.[0] ?? null;
              e.target.value = '';
              void handleFile(file);
            }}
          />
        </div>
      )}

      {tab === 'url' && (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              URL da imagem
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleConfirmUrl}>
              Inserir
            </Button>
          </div>
        </div>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </Modal>
  );
};
